import {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import {db} from '../firebase-config';
import {useAuth} from '../contexts/AuthContext';
import {Alert, Box, Button, Chip, CircularProgress, Grid, Paper, Rating, Typography} from '@mui/material';
import {AccessTime, Delete, Edit, PlayArrow, Psychology, Star, TrendingUp} from '@mui/icons-material';
import UserAvatar from '../components/UserAvatar';
import {formatDate, formatTime} from '../utils/dateUtils';
import exampleQuiz from '../img/quiz/example-quiz.jpg';
import deutschlandQuiz from '../img/quiz/deutschland-quiz.jpg';
import fortniteQuiz from '../img/quiz/fortnite-battle-royale-quiz.jpg';
import harryPotterQuiz from '../img/quiz/harry-potter-quiz.jpg';
import internetMemeQuiz from '../img/quiz/internet-meme-quiz.jpg';
import kurioseFaktenQuiz from '../img/quiz/kuriose-fakten-quiz.jpg';
import marvelSuperheldenQuiz from '../img/quiz/marvel-superhelden-quiz.jpg';
import pixarFilmquiz from '../img/quiz/pixar-filmquiz.jpg';
import skibidiToiletQuiz from '../img/quiz/skibidi-toilet-quiz.jpg';
import defaultQuizImage from '../img/quiz/default.jpg';

// Funktion zum Generieren eines zufälligen Lobby-Codes
function generateLobbyCode() {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

export default function QuizDetails() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentLobby] = useState(null);
  const [ratingStats, setRatingStats] = useState({ average: 0, total: 0 });
  const [ratings, setRatings] = useState([]);

  useEffect(() => {
    async function fetchQuizDetails() {
      try {
        setLoading(true);
        setError(null);

        // Quiz-Daten laden
        const quizDoc = await getDoc(doc(db, 'quizzes', quizId));
        if (!quizDoc.exists()) {
          setError('Quiz nicht gefunden');
          return;
        }

        const quizData = { id: quizDoc.id, ...quizDoc.data() };

        // Game Sessions laden und Statistiken aktualisieren
        const gameSessionsQuery = query(
          collection(db, 'game_sessions'),
          where('currentQuiz.quizId', '==', quizId),
          where('status', '==', 'finished')
        );

        const sessionsSnapshot = await getDocs(gameSessionsQuery);

        // Berechne die Gesamtstatistiken
        let totalScore = 0;
        let totalCorrectAnswers = 0;
        let totalAnswers = 0;
        let totalCompletionTime = 0;

        sessionsSnapshot.forEach(doc => {
          const session = doc.data();
          Object.values(session.players || {}).forEach(player => {
            totalScore += player.score || 0;
            totalCorrectAnswers += player.correctAnswers || 0;
            totalAnswers += player.answers?.length || 0;
          });

          if (session.currentQuiz?.startedAt && session.currentQuiz?.endedAt) {
            const startTime = session.currentQuiz.startedAt.toDate();
            const endTime = session.currentQuiz.endedAt.toDate();
            totalCompletionTime += (endTime - startTime) / 1000;
          }
        });

        // Berechne Durchschnittswerte
        const totalPlays = sessionsSnapshot.size;
        const averageScore = totalPlays > 0 ? Math.round(totalScore / totalPlays) : 0;
        const correctAnswersPercent = totalAnswers > 0 ? Math.round((totalCorrectAnswers / totalAnswers) * 100) : 0;
        const averageCompletionTime = totalPlays > 0 ? Math.round(totalCompletionTime / totalPlays) : 0;

        // Behalte die bestehenden Rating-Statistiken bei
        const currentStats = quizData?.stats || {};

        // Erstelle die Update-Objekte
        const statsUpdate = {
          totalPlays: totalPlays,
          totalScore: totalScore,
          averageScore: averageScore,
          correctAnswersPercent: correctAnswersPercent,
          averageRating: currentStats.averageRating || 0,
          totalRatings: currentStats.totalRatings || 0,
          positiveRatingPercent: currentStats.positiveRatingPercent || 0,
          ratingDistribution: currentStats.ratingDistribution || {}
        };

        const metadataUpdate = {
          timesPlayed: totalPlays,
          averageCompletionTime: averageCompletionTime,
          lastPlayed: serverTimestamp()
        };

        // Aktualisiere die Quiz-Daten
        const quizRef = doc(db, 'quizzes', quizId);
        await updateDoc(quizRef, {
          stats: statsUpdate,
          metadata: metadataUpdate,
          lastUpdated: serverTimestamp()
        });

        // Setze die aktualisierten Daten
        setQuiz({
          ...quizData,
          stats: statsUpdate,
          metadata: metadataUpdate
        });

        // Bewertungen direkt laden statt über onSnapshot
        const feedbackQuery = query(
          collection(db, 'quiz_feedback'),
          where('quizId', '==', quizId),
          orderBy('timestamp', 'desc'),
          limit(10)
        );

        const feedbackSnapshot = await getDocs(feedbackQuery);
        const feedbackData = await Promise.all(feedbackSnapshot.docs.map(async feedbackDoc => {
          const data = feedbackDoc.data();
          // Benutzer-Daten aus Firestore laden
          try {
            const userRef = doc(db, 'users', data.userId);
            const userDoc = await getDoc(userRef);
            const userData = userDoc.exists() ? userDoc.data() : {};

            return {
              id: feedbackDoc.id,
              ...data,
              timestamp: data.timestamp?.toDate(),
              hasRpmAvatar: userData.hasRpmAvatar || false,
              rpmModelUrl: userData.rpmModelUrl || null,
              rpmAvatarConfig: userData.rpmAvatarConfig || null,
              rpmAvatarType: userData.rpmAvatarType || null,
              userPhotoURL: userData.photoURL || data.userPhotoURL || '',
              userDisplayName: userData.displayName || data.userDisplayName || 'Unbekannt',
              profilePath: userData.profilePath || null
            };
          } catch (error) {
            console.error('Fehler beim Laden der Benutzerdaten:', error);
            return {
              id: feedbackDoc.id,
              ...data,
              timestamp: data.timestamp?.toDate(),
              hasRpmAvatar: false,
              rpmModelUrl: null,
              rpmAvatarConfig: null,
              rpmAvatarType: null,
              userPhotoURL: data.userPhotoURL || '',
              userDisplayName: data.userDisplayName || 'Unbekannt',
              profilePath: null
            };
          }
        }));

        setRatings(feedbackData);

        // Bewertungsstatistiken berechnen
        const ratings = feedbackData.map(f => f.rating).filter(r => r !== undefined);
        const totalRatings = ratings.length;
        const averageRating = totalRatings > 0
          ? ratings.reduce((a, b) => a + b, 0) / totalRatings
          : 0;

        const distribution = {};
        ratings.forEach(rating => {
          distribution[rating] = (distribution[rating] || 0) + 1;
        });

        setRatingStats({
          average: averageRating,
          total: totalRatings,
          ...distribution
        });

      } catch (err) {
        console.error('Fehler beim Laden des Quiz:', err);
        setError('Fehler beim Laden des Quiz');
      } finally {
        setLoading(false);
      }
    }

    if (quizId) {
      fetchQuizDetails();
    }
  }, [quizId]);

  const handleQuizSelect = async () => {
    try {
      const lobbyId = generateLobbyCode();
      const gameSessionRef = doc(collection(db, 'game_sessions'), lobbyId);
      await setDoc(gameSessionRef, {
        status: 'selecting_quiz',
        createdAt: serverTimestamp(),
        gameMasterId: currentUser.uid,
        currentQuiz: {
          quizId: quizId,
          currentQuestion: 0,
          startedAt: null,
          endedAt: null
        },
        players: {
          [currentUser.uid]: {
            displayName: currentUser.displayName || 'Unbenannter Spieler',
            photoURL: currentUser.photoURL,
            hasRpmAvatar: currentUser.hasRpmAvatar || false,
            rpmModelUrl: currentUser.rpmModelUrl || null,
            rpmAvatarConfig: currentUser.rpmAvatarConfig || null,
            rpmAvatarType: currentUser.rpmAvatarType || null,
            isReady: false,
            score: 0,
            answers: [],
            correctAnswers: 0,
            isGameMaster: true,
            lastActive: serverTimestamp()
          }
        },
        lastActivity: serverTimestamp()
      });
      navigate(`/lobby/${lobbyId}`);
    } catch (error) {
      console.error('Fehler beim Erstellen der Lobby:', error);
      setError('Fehler beim Erstellen der Lobby');
    }
  };

  const getQuizImage = (quiz) => {
    // Wenn es ein benutzerdefiniertes Quiz mit imageUrl ist
    if (quiz.createdBy && quiz.imageUrl) {
      return quiz.imageUrl;
    }

    // Für vordefinierte Quizzes
    switch (quiz.id) {
      case 'example-quiz': return exampleQuiz;
      case 'deutschland-quiz': return deutschlandQuiz;
      case 'fortnite-battle-royale-quiz': return fortniteQuiz;
      case 'harry-potter-quiz': return harryPotterQuiz;
      case 'internet-meme-quiz': return internetMemeQuiz;
      case 'kuriose-fakten-quiz': return kurioseFaktenQuiz;
      case 'marvel-superhelden-quiz': return marvelSuperheldenQuiz;
      case 'pixar-filmquiz': return pixarFilmquiz;
      case 'skibidi-toilet-quiz': return skibidiToiletQuiz;
      default: return defaultQuizImage;
    }
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!quiz) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Quiz nicht gefunden
      </Alert>
    );
  }

  return (
    <div className="container-fixed">
      {quiz && (
        <>
          <Box
            className="quiz-details-header"
            sx={{
              backgroundImage: `url(${getQuizImage(quiz)})`,
              // ... rest of the styles ...
            }}
          >
            {/* Oberer Bereich mit Zurück-Button */}
            <Box sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              position: 'relative',
              zIndex: 2
            }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={() => navigate('/')}
                  className="quiz-details-back-button"
                >
                  Zurück zur Übersicht
                </Button>
                {currentUser && quiz.createdBy === currentUser.uid && (
                  <>
                    <Button
                      variant="contained"
                      onClick={() => navigate(`/quiz/edit/${quiz.id}`)}
                      className="quiz-details-back-button"
                      startIcon={<Edit />}
                    >
                      Bearbeiten
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={async () => {
                        if (window.confirm('Möchtest du dieses Quiz wirklich löschen?')) {
                          try {
                            await deleteDoc(doc(db, 'quizzes', quiz.id));
                            navigate('/');
                          } catch (error) {
                            console.error('Fehler beim Löschen des Quiz:', error);
                            setError('Fehler beim Löschen des Quiz');
                          }
                        }
                      }}
                      className="quiz-details-back-button"
                      startIcon={<Delete />}
                    >
                      Löschen
                    </Button>
                  </>
                )}
              </Box>
            </Box>

            {/* Unterer Bereich mit Titel und Action Button */}
            <Box className="quiz-details-header-gradient">
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} md={8}>
                  <Typography variant="h3" className="quiz-details-title">
                    {quiz.title}
                  </Typography>
                  <Typography variant="body1" className="quiz-details-description">
                    {quiz.description}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    alignItems: 'flex-end'
                  }}>
                    {(quiz.categories || []).map((category, index) => (
                      <Chip
                        key={index}
                        label={category}
                        size="small"
                        className="quiz-details-category"
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* Statistiken Box */}
          <Paper className="quiz-details-stats-container">
            <Grid container>
              {/* Lobby erstellen Button */}
              <Grid item style={{ width: '20%' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrow sx={{
                    fontSize: '35px',
                    width: '35px',
                    height: '35px'
                  }} />}
                  onClick={handleQuizSelect}
                  className="quiz-details-action-button"
                >
                  {currentLobby ? 'Quiz wählen' : 'Lobby erstellen'}
                </Button>
              </Grid>

              {/* Beliebtheit */}
              <Grid item style={{ width: '20%' }}>
                <Box className="quiz-details-stat-box">
                  <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%'
                  }}>
                    <Rating
                      value={ratingStats.average || 0}
                      precision={0.5}
                      readOnly
                      size="large"
                      classes={{
                        iconFilled: 'quiz-details-rating-filled',
                        iconEmpty: 'quiz-details-rating-empty'
                      }}
                      sx={{
                        '& .MuiRating-icon': {
                          fontSize: '2.5rem'
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Grid>

              {/* Aktivität */}
              <Grid item style={{ width: '20%' }}>
                <Box className="quiz-details-stat-box">
                  <Box className="quiz-details-stat-content">
                    <TrendingUp className="quiz-details-stat-icon" sx={{
                      color: quiz.metadata?.timesPlayed > 100 ? 'rgba(76, 175, 80, 0.9)' :
                          quiz.metadata?.timesPlayed > 50 ? 'rgba(255, 193, 7, 0.9)' :
                          quiz.metadata?.timesPlayed > 10 ? 'rgba(255, 152, 0, 0.9)' :
                          'var(--color-secondary)'
                    }} />
                    <Box>
                        <Typography variant="h6" className="quiz-details-stat-title" sx={{
                            color: quiz.metadata?.timesPlayed > 100 ? 'rgba(76, 175, 80, 0.9)' :
                                quiz.metadata?.timesPlayed > 50 ? 'rgba(255, 193, 7, 0.9)' :
                                    quiz.metadata?.timesPlayed > 10 ? 'rgba(255, 152, 0, 0.9)' :
                                        'var(--color-secondary)'
                        }}>
                        {quiz.metadata?.timesPlayed > 100 ? 'Sehr beliebtes Quiz' :
                         quiz.metadata?.timesPlayed > 50 ? 'Beliebtes Quiz' :
                         quiz.metadata?.timesPlayed > 10 ? 'Oft gespielt' :
                         'Neues Quiz'}
                      </Typography>
                      <Typography variant="body2" className="quiz-details-stat-text">
                        {quiz.metadata?.timesPlayed || 0}x gespielt
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Schwierigkeitsgrad */}
              <Grid item style={{ width: '20%' }}>
                <Box className="quiz-details-stat-box">
                  <Box className="quiz-details-stat-content">
                      <Psychology className="quiz-details-stat-icon" sx={{
                          color: quiz.stats?.correctAnswersPercent > 80 ? 'rgba(76, 175, 80, 0.9)' :
                              quiz.stats?.correctAnswersPercent > 60 ? 'rgba(255, 193, 7, 0.9)' :
                                  quiz.stats?.correctAnswersPercent > 40 ? 'rgba(255, 152, 0, 0.9)' :
                                      'rgba(244, 67, 54, 0.9)'
                      }} />
                    <Box>
                        <Typography variant="h6" className="quiz-details-stat-title" sx={{
                            color: quiz.stats?.correctAnswersPercent > 80 ? 'rgba(76, 175, 80, 0.9)' :
                                quiz.stats?.correctAnswersPercent > 60 ? 'rgba(255, 193, 7, 0.9)' :
                                    quiz.stats?.correctAnswersPercent > 40 ? 'rgba(255, 152, 0, 0.9)' :
                                        'rgba(244, 67, 54, 0.9)'
                        }}>
                        {quiz.stats?.correctAnswersPercent > 80 ? 'Leicht' :
                         quiz.stats?.correctAnswersPercent > 60 ? 'Ausgewogen' :
                         quiz.stats?.correctAnswersPercent > 40 ? 'Anspruchsvoll' :
                         'Schwierig'}
                      </Typography>
                      <Typography variant="body2" className="quiz-details-stat-text">
                        {quiz.stats?.correctAnswersPercent || 0}% der Antworten richtig
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Zeitaufwand */}
              <Grid item style={{ width: '20%' }}>
                <Box className="quiz-details-stat-box">
                  <Box className="quiz-details-stat-content">
                      <AccessTime className="quiz-details-stat-icon" sx={{
                          color: quiz.metadata?.averageCompletionTime > 900 ? 'rgba(244, 67, 54, 0.9)' :
                              quiz.metadata?.averageCompletionTime > 600 ? 'rgba(255, 152, 0, 0.9)' :
                                  quiz.metadata?.averageCompletionTime > 300 ? 'rgba(255, 193, 7, 0.9)' :
                                      'rgba(76, 175, 80, 0.9)'
                      }} />
                    <Box>
                        <Typography variant="h6" className="quiz-details-stat-title" sx={{
                            color: quiz.metadata?.averageCompletionTime > 900 ? 'rgba(244, 67, 54, 0.9)' :
                                quiz.metadata?.averageCompletionTime > 600 ? 'rgba(255, 152, 0, 0.9)' :
                                    quiz.metadata?.averageCompletionTime > 300 ? 'rgba(255, 193, 7, 0.9)' :
                                        'rgba(76, 175, 80, 0.9)'
                        }}>
                        {quiz.metadata?.averageCompletionTime > 900 ? 'Ausführliches Quiz' :
                         quiz.metadata?.averageCompletionTime > 600 ? 'Umfangreiches Quiz' :
                         quiz.metadata?.averageCompletionTime > 300 ? 'Moderater Umfang' :
                         'Schnelles Quiz'}
                      </Typography>
                      <Typography variant="body2" className="quiz-details-stat-text">
                        {formatTime(quiz.metadata?.averageCompletionTime || 0)} Spielzeit
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Ratings Box */}
          <Box className="quiz-details-rating-section">
            <Paper className="quiz-details-rating-container">
              <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      className="quiz-details-icon"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                        fontWeight: 'bold'
                      }}
                    >
                      <Star fontSize="small" />
                      {ratingStats.total || 0} Bewertungen
                    </Typography>

                    {/* Rating Distribution */}
                    <Box sx={{ mt: 2 }}>
                      {[5, 4, 3, 2, 1].map((rating) => (
                        <Box
                          key={rating}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 1
                          }}
                        >
                          <Typography sx={{ minWidth: 20, mr: 0.5 }}>{rating}</Typography>
                          <Star fontSize="small" className="quiz-details-rating-filled" />
                          <Box
                            sx={{
                              flex: 1,
                              height: '8px',
                              borderRadius: '4px',
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                              overflow: 'hidden',
                              ml: 1
                            }}
                          >
                            <Box
                              sx={{
                                width: `${((ratingStats[rating] || 0) / (ratingStats.total || 1)) * 100}%`,
                                height: '100%',
                                bgcolor: 'var(--color-secondary)',
                                borderRadius: 'inherit'
                              }}
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>

                {/* Right Column: Rating List */}
                <Grid item xs={12} md={8}>
                  <Box>
                    {ratings.length > 0 ? (
                      <Grid container spacing={2}>
                        {ratings.map((item, index) => (
                          <Grid item xs={12} md={6} key={index}>
                            <Paper className="quiz-details-rating-box">
                              <Box className="quiz-details-rating-comment">
                                <Box className="quiz-details-rating-stars">
                                  <Rating
                                    value={item.rating}
                                    readOnly
                                    size="medium"
                                    classes={{
                                      iconFilled: 'quiz-details-rating-filled',
                                      iconEmpty: 'quiz-details-rating-empty'
                                    }}
                                  />
                                </Box>
                                {item.feedback && (
                                  <Typography
                                    variant="body1"
                                    className="quiz-details-feedback"
                                    sx={{ mt: 1.5, mb: 3 }}
                                  >
                                    {item.feedback}
                                  </Typography>
                                )}
                                <Box className="quiz-details-rating-header">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {item.profilePath ? (
                                      <Link
                                        to={`/profil/${item.profilePath}`}
                                        style={{
                                          textDecoration: 'none',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px'
                                        }}
                                      >
                                        <UserAvatar
                                          src={item.userPhotoURL}
                                          alt={item.userDisplayName}
                                          size={32}
                                          hasRpmAvatar={item.hasRpmAvatar}
                                          rpmModelUrl={item.rpmModelUrl}
                                          rpmAvatarConfig={item.rpmAvatarConfig}
                                          rpmAvatarType={item.rpmAvatarType}
                                          sx={{
                                            border: '2px solid rgba(255, 255, 255, 0.1)',
                                            '&:hover': {
                                              borderColor: 'var(--color-secondary)'
                                            }
                                          }}
                                        />
                                        <Typography
                                          variant="subtitle2"
                                          noWrap
                                          className="quiz-details-text-primary"
                                          sx={{
                                            '&:hover': {
                                              color: 'var(--color-secondary)'
                                            }
                                          }}
                                        >
                                          {item.userDisplayName}
                                        </Typography>
                                      </Link>
                                    ) : (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <UserAvatar
                                          src={item.userPhotoURL}
                                          alt={item.userDisplayName}
                                          size={32}
                                          hasRpmAvatar={item.hasRpmAvatar}
                                          rpmModelUrl={item.rpmModelUrl}
                                          rpmAvatarConfig={item.rpmAvatarConfig}
                                          rpmAvatarType={item.rpmAvatarType}
                                          sx={{
                                            border: '2px solid rgba(255, 255, 255, 0.1)',
                                            '&:hover': {
                                              borderColor: 'var(--color-secondary)'
                                            }
                                          }}
                                        />
                                        <Typography
                                          variant="subtitle2"
                                          noWrap
                                          className="quiz-details-text-primary"
                                        >
                                          {item.userDisplayName}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    className="quiz-details-text-secondary"
                                  >
                                    {formatDate(item.timestamp)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography
                        variant="body1"
                        className="quiz-details-text-secondary"
                        sx={{ textAlign: 'center', py: 4 }}
                      >
                        Noch keine Bewertungen vorhanden
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </>
      )}
    </div>
  );
} 